import { DOCUMENT } from "@angular/common";
import { Inject, Injectable } from "@angular/core";
import { EventManagerPlugin } from "@angular/platform-browser";
import { UtilsService } from "src/app/services";

// Module-level guard: when a pointer-up handler fires for a Angular (click)
// binding, the handler may modify the DOM (open/close a modal, navigate, etc.)
// before the synthesized click event fires. The click then lands on whatever
// is at the touch coordinate AFTER the DOM change, triggering unrelated
// handlers (e.g., the delete-bulk button fires when closing the edit modal).
// We swallow the next click after any pointer-up handler runs so this can't
// happen.
let suppressNextClick = false;
let suppressTimer: number | null = null;
let globalSuppressorInstalled = false;

const isFormControl = (target: EventTarget | null): boolean => (
  target instanceof HTMLInputElement ||
  target instanceof HTMLSelectElement ||
  target instanceof HTMLTextAreaElement
);

const installGlobalClickSuppressor = (doc: Document): void => {
  if (globalSuppressorInstalled) return;
  globalSuppressorInstalled = true;
  doc.addEventListener('click', (e: Event) => {
    if (suppressNextClick) {
      suppressNextClick = false;
      if (suppressTimer !== null) { window.clearTimeout(suppressTimer); suppressTimer = null; }
      // Don't suppress clicks on form controls — their default action
      // (toggling a checkbox, focusing an input) is what the user expects.
      if (isFormControl(e.target)) return;
      e.stopPropagation();
      // Don't preventDefault — that would also block form-submit / anchor
      // navigation when the user wants it; stopPropagation is enough to
      // prevent ancestor handlers from firing.
    }
  }, true);  // capture phase so we beat any other handler
};

@Injectable()
export class PointerClickPlugin extends EventManagerPlugin {
  constructor(@Inject(DOCUMENT) doc: any, public utilsService: UtilsService) {
    super(doc);
    if (utilsService.isTouchDevice()) installGlobalClickSuppressor(doc);
  }

  supports(eventName: string): boolean {
    return eventName === 'click';
  }

  addEventListener(element: HTMLElement, eventName: string, handler: Function): Function {
    if (this.utilsService.isTouchDevice()) {
      // --- TABLETS / MOBILE ---
      let handledByPointer = false;

      const pointerListener = (event: Event) => {
        // For form controls, let the native click drive the handler. Their
        // post-state (e.g., checkbox.checked) is only updated when click
        // fires, not on pointerup — running the handler now would read the
        // stale pre-toggle value.
        if (isFormControl(element)) return;

        handledByPointer = true;
        handler(event);
        // Arm the global suppressor: the click event that follows this
        // pointer-up should be swallowed (see comment above the global flag).
        // Cleared after 100ms in case no synthesized click arrives.
        suppressNextClick = true;
        if (suppressTimer !== null) window.clearTimeout(suppressTimer);
        suppressTimer = window.setTimeout(() => {
          suppressNextClick = false;
          suppressTimer = null;
        }, 100);
      };

      const clickListener = (event: Event) => {
        // Document/window targets don't have a tagName; guard against undefined.
        if (element instanceof HTMLAnchorElement) {
          event.preventDefault();
        }

        // Always reset on entry so a stale `handledByPointer` from a prior
        // event can't leak into this one. (The outer flag is set by
        // pointerListener; once the click arrives, we're done with it.)
        const wasHandledByPointer = handledByPointer;
        handledByPointer = false;

        // If a descendant element already ran its handler via pointerup AND
        // called stopPropagation on the pointer event, the pointer-up didn't
        // reach us — but the synthesized click still bubbles up. Without this
        // check the parent's handler would also fire (e.g. tapping a "see
        // routes" button would also trigger the row's "open detail" handler).
        if ((event as { __handledByPointerChild?: boolean }).__handledByPointerChild) {
          return;
        }

        if (wasHandledByPointer) {
          // We handled it on pointerup. Mark the click so ancestor
          // clickListeners know to skip themselves.
          (event as { __handledByPointerChild?: boolean }).__handledByPointerChild = true;
          return;
        }

        handler(event);
      };

      element.addEventListener('pointerup', pointerListener, false);
      element.addEventListener('click', clickListener, false);

      return () => {
        element.removeEventListener('pointerup', pointerListener, false);
        element.removeEventListener('click', clickListener, false);
      };
    } else {
      // --- DESKTOPS ---
      const clickListener = (event: Event) => handler(event);
      element.addEventListener('click', clickListener, false);

      return () => {
        element.removeEventListener('click', clickListener, false);
      };
    }
  }
}